<script setup lang="ts">
import CustomButton from '@/components/CustomButton.vue';
import UpsertSourceDialog from '@/components/dialogs/UpsertSourceDialog.vue';
import List from '@/components/List.vue';
import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';
import { useSourceStore } from '@/stores/source';
import { TypeOfOrganization } from '@/types/organization';
import type { Source } from '@/types/source';
import { onMounted, ref, watch } from 'vue';

const sourceStore = useSourceStore();
const authStore = useAuthStore();

const globalStore = useGlobalStore();
const isLoading = ref(false);
const isUpsertDialogOpen = ref(false);
const selectedSource = ref<Source | null>(null);

function isOrganizationSource(item: Source) {
    return !!item.organization;
}

const listHeaders = [
    { title: 'Naam', value: 'name', sortable: true },
    {
        title: 'Type',
        key: 'isDefault',
        sortable: true
    }
];

onMounted(async () => {
    isLoading.value = true;
    await sourceStore.getByOrganizationId(authStore.getCurrentOrganization()._id);
    await sourceStore.getGlobalSources();
    isLoading.value = false;
});

watch(
    () => authStore.getCurrentOrganization()._id,
    async () => {
        sourceStore.organizationSources = [];
        sourceStore.organizationSources = await sourceStore.getByOrganizationId(authStore.getCurrentOrganization()._id);
    }
);

async function deleteOrganizationSource(item: Source) {
    await sourceStore.remove(item._id);
}

function openUpsertDialog(item: Source) {
    selectedSource.value = item;
    isUpsertDialogOpen.value = true;
}
</script>

<template>
    <v-fade-transition>
        <div v-show="!isLoading && !globalStore.isLoading">
            <v-row>
                <CustomButton
                    text="Bron toevoegen"
                    @click="isUpsertDialogOpen = true"
                    v-if="authStore.getCurrentOrganization()?.type === TypeOfOrganization.COMPANY"
                />
                <span v-else>
                    Let op: bedrijfsspecifieke bronnen kunnen alleen op bedrijfsniveau worden beheerd. Wijzigen vanuit een vestiging is niet mogelijk.
                </span>
            </v-row>
            <List
                v-if="sourceStore.allSources.length > 0"
                :items="sourceStore.allSources"
                :headers="listHeaders"
                showSearch
                entityName="Bron"
                @delete-confirmed="deleteOrganizationSource"
                :is-deletable="true"
                :deleteEnabled="isOrganizationSource"
                @click-table-row-edit="openUpsertDialog"
                is-editable
                :editEnabled="isOrganizationSource"
            >
                <template #item.isDefault="{ item }">
                    <template v-if="item.organization">
                        <span class="py-1 px-2 rounded bg-main-lighter">Bedrijfsspecifiek</span>
                    </template>
                    <template v-else>
                        <v-tooltip text="Standaard bronnen zijn door 't Web aangemaakt en kunnen niet worden aangepast.">
                            <template #activator="{ props }">
                                <span v-bind="props" class="bg-grey-lighten-1 py-1 px-2 rounded"> Standaard </span>
                            </template>
                        </v-tooltip>
                    </template>
                </template>
            </List>
            <p v-else-if="!globalStore.isLoading">Geen bronnen gevonden</p>
            <UpsertSourceDialog
                :modelValue="isUpsertDialogOpen"
                :organizationId="authStore.getCurrentOrganization()._id"
                :source="selectedSource"
                @closeDialog="isUpsertDialogOpen = false"
            />
        </div>
    </v-fade-transition>
</template>
